import { Country } from '@/types/types';
import { EVENT_PLATFORMS, EVENTS_ORG, LoginMethods } from '@pw-tech/web-sdk';

export const PER_PAGE = 24;

export const TABS = ['upload', 'general'] as const;

export const ACCEPTED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
  'application/msword',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'audio/mpeg',
  'audio/wav',
  'audio/ogg',
  'video/mp4',
  'video/mpeg',
  'video/quicktime',
];

export enum OrderDirection {
  ASC = 'asc',
  DESC = 'desc',
}

export enum OrderBy {
  CREATED = 'createdAt',
  UPDATED = 'updatedAt',
}

export enum MediaFileType {
  IMAGE = 'image/',
  VIDEO = 'video/',
  AUDIO = 'audio/',
  PDF = 'application/pdf',
}

export enum FileExtensions {
  PDF = 'pdf',
  DOC = 'doc',
  DOCX = 'docx',
  MSWORD = 'msword',
  XLS = 'xls',
  XLSX = 'xlsx',
  CSV = 'csv',
  MP4 = 'mp4',
  WEBM = 'webm',
  OGG = 'ogg',
  MOV = 'mov',
  MKV = 'mkv',
  MP3 = 'mp3',
  MPEG = 'mpeg',
  WAV = 'wav',
  M4A = 'm4a',
  JPG = 'jpg',
  JPEG = 'jpeg',
  PNG = 'png',
  GIF = 'gif',
  WEBP = 'webp',
  BMP = 'bmp',
  TIFF = 'tiff',
}

export const ImageExtensions = [
  FileExtensions.JPG,
  FileExtensions.JPEG,
  FileExtensions.PNG,
  FileExtensions.GIF,
  FileExtensions.WEBP,
  FileExtensions.BMP,
  FileExtensions.TIFF,
];

export const VideoExtensions = [
  FileExtensions.MP4,
  FileExtensions.WEBM,
  FileExtensions.OGG,
  FileExtensions.MOV,
  FileExtensions.MKV,
  FileExtensions.MPEG,
];

export const AudioExtensions = [
  FileExtensions.MP3,
  FileExtensions.WAV,
  FileExtensions.M4A,
  FileExtensions.OGG,
];

export const DocumentExtensions = [FileExtensions.PDF, FileExtensions.DOC, FileExtensions.DOCX];

export const SpreadsheetExtensions = [FileExtensions.XLS, FileExtensions.XLSX, FileExtensions.CSV];

export enum MediaType {
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  DOCUMENT = 'document',
  SPREADSHEET = 'spreadsheet',
  UNKNOWN = 'unknown',
}

export enum MediaTabs {
  UPLOAD = 'upload',
  MEDIA = 'media',
}

export const OTHER_COUNTRY: Country = {
  _id: 'other',
  countryCode: '+91',
  countryGroup: 'IN',
  countryName: 'OTHER COUNTRY',
  createdAt: '',
  flag: 'https://static.pw.live/5eb393ee95fab7468a79d189/GLOBAL_CMS/9349dd11-e965-4e70-9760-1b4564654227.webp',
  isActive: true,
  status: true,
  updatedAt: '',
  name: 'OTHER COUNTRY',
};

export const colorsPrimaryNames = ['yellow', 'orange', 'violet', 'red', 'blue', 'green'];

export const curiousJrBatchIds = ['6733803c20cd02040f0a921e'];

export const DocumentMimeTypes = ['application/pdf', 'application/msword'] as const;
export const SpreadsheetMimeSubtypes = [
  'octet-stream',
  'vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'vnd.ms-excel',
] as const;

export const MEDIA_UPLOADER_ICON = `
<svg width="24" height="24" viewBox="0 0 24 24">
  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-5-7l-3 3.72L9 13l-3 4h12l-4-5z"/>
</svg>
`;
export const HOSTNAMES = {
  CURIOUS_JR: 'curiousjr.com',
  PHYSICS_WALLAH: 'pw.live',
  PHYSICS_WALLAH_MEDHARTHI: 'pwmedharthi.com',
  PHYSICS_WALLAH_IOI: 'pwioi.com',
};

export const ORG_CONFIG_MAP = {
  [HOSTNAMES.CURIOUS_JR]: {
    organizationId: process.env.NEXT_PUBLIC_CURIOUSJR_ORG_ID || '',
    organizationName: EVENTS_ORG.CURIOUS_JR,
    apiBaseURL: process.env.NEXT_PUBLIC_API_BASE_URL_AUTH || '',
    loginMethods: [LoginMethods.OTP],
    contextIdentifier: 'TOKEN_CONTEXT',
    eventPlatforms: [EVENT_PLATFORMS.GA, EVENT_PLATFORMS.MOENGAGE],
    clarityKey: 'oxgzmfeua8',
    subOrgId: 'SUB-PWLI000',
  },
  [HOSTNAMES.PHYSICS_WALLAH]: {
    organizationId: process.env.NEXT_PUBLIC_PWLIVE_ORG_ID || '',
    organizationName: EVENTS_ORG.PHYSICS_WALLAH,
    apiBaseURL: process.env.NEXT_PUBLIC_API_BASE_URL_AUTH || '',
    loginMethods: [LoginMethods.OTP, LoginMethods.PASSWORD],
    contextIdentifier: 'TOKEN_CONTEXT',
    eventPlatforms: [EVENT_PLATFORMS.GA],
    clarityKey: 'pydpgf4cyi',
    subOrgId: 'SUB-PWLI000',
  },
  [HOSTNAMES.PHYSICS_WALLAH_MEDHARTHI]: {
    organizationId: process.env.NEXT_PUBLIC_MEDHARTHI_ORG_ID || '',
    organizationName: EVENTS_ORG.PHYSICS_WALLAH,
    apiBaseURL: process.env.NEXT_PUBLIC_API_BASE_URL_AUTH || '', // For medharthi, using the same base URL as PW.
    loginMethods: [LoginMethods.OTP, LoginMethods.PASSWORD],
    contextIdentifier: 'TOKEN_CONTEXT',
    eventPlatforms: [EVENT_PLATFORMS.GA],
    clarityKey: 's0aovhv3qm',
    subOrgId: 'SUB-PWLI000',
  },
  [HOSTNAMES.PHYSICS_WALLAH_IOI]: {
    organizationId: process.env.NEXT_PUBLIC_PWLIVE_ORG_ID || '',
    organizationName: EVENTS_ORG.PHYSICS_WALLAH,
    apiBaseURL: process.env.NEXT_PUBLIC_API_BASE_URL_AUTH || '',
    loginMethods: [LoginMethods.OTP, LoginMethods.PASSWORD],
    contextIdentifier: 'TOKEN_CONTEXT',
    eventPlatforms: [EVENT_PLATFORMS.GA],
    clarityKey: 'pydpgf4cyi',
    subOrgId: 'SUB-PWII011',
  },
  default: {
    organizationId: process.env.NEXT_PUBLIC_PWLIVE_ORG_ID || '',
    organizationName: EVENTS_ORG.PHYSICS_WALLAH,
    apiBaseURL: process.env.NEXT_PUBLIC_API_BASE_URL_AUTH || '',
    loginMethods: [LoginMethods.OTP, LoginMethods.PASSWORD],
    contextIdentifier: 'TOKEN_CONTEXT',
    eventPlatforms: [EVENT_PLATFORMS.GA],
    clarityKey: 'pydpgf4cyi',
    subOrgId: 'SUB-PWLI000',
  },
};

export const UNLEASH_EXPERIMENTS = {
  CJR_STRIPE_IN_MODAL: 'cjr_stripe_in_modal',
  CJR_PRE_BOOSTER_MEGA_VISIBILITY: 'cjr-pre-booster-mega-visibility',
  CJR_STORE: 'cjr-store',
};

export const CJR_STORE = {
  enabled: 'ENABLED',
  disabled: 'DISABLED',
};

export const CJR_STRIPE_IN_MODAL = {
  enabled: 'STRIPE_IN_MODAL_ENABLED',
  disabled: 'STRIPE_IN_MODAL_DISABLED',
};

export const STRIPE_KEY = {
  STRIPE_CLIENT_SECRET: 'stripeClientSecret',
  OPEN_STRIPE_CHECKOUT: 'open-stripe-checkout',
};

export const CJR_PRE_BOOSTER_MEGA_VISIBILITY = {
  target: 'cjr-pre-booster-mega-visibility_target',
  control: 'cjr-pre-booster-mega-visibility_control',
  disabled: 'cjr-pre-booster-mega-visibility_disabled',
};

/** Unleash variant names for cjr-pre-booster-mega-visibility (use for comparison with variant.name) */
export enum PreBoosterMegaVariant {
  Target = 'target',
  Control = 'control',
  Disabled = 'disabled',
}
